/**
 * TargetOnlyCell — a month that has a plan but no actual yet (every future month).
 *
 * Targets are served for the whole year while actuals stop at the last closed month, so
 * keying the cell off the actual — as the matrix did until 10 Aug 2026 — threw a served
 * value away and rendered the month as though nothing were known about it. The incumbent
 * report shows those months (its Plan and Actual are separate ROWS, so a future month has
 * a populated Plan above a blank Actual); the matrix compresses both into one cell, which
 * is exactly why it has to say which half is missing rather than dropping the cell.
 *
 * So: the em-dash still means "no actual", and the target sits beneath it as on any other
 * cell. No RAG (nothing to compare), no lock glyph, no drill affordance — the envelope
 * those come from belongs to the actual, and there isn't one.
 */

import React from 'react'
import type { Cell } from '../../types/metrics'
import { EM_DASH, formatCellValue, isCell } from './cellFormat'

export default function TargetOnlyCell({ target }: { target: Cell }): React.JSX.Element {
  if (!isCell(target)) return <span className="text-[10px] italic text-bad">invalid cell</span>
  return (
    <div
      data-testid="target-only-cell"
      data-rag="none"
      title={`No actual served for ${target.period} — plan only`}
      style={{ borderLeft: '2.5px solid var(--neutral)' }}
      className="flex flex-col items-end gap-px rounded-sm py-1.5 pl-2 pr-2.5"
    >
      <span className="font-semibold tabular-nums text-mut2">{EM_DASH}</span>
      <span className="mt-0.5 font-mono text-[10px] tabular-nums text-mut2">
        tgt {formatCellValue(target)}
      </span>
    </div>
  )
}
